import { DocumentType, modelOptions, PropType, post, pre, prop, type Ref } from "@typegoose/typegoose";
import { Severity } from "@typegoose/typegoose/lib/internal/constants.js";
import mongoose from "mongoose";
import { z } from "zod";
import { EventInstance } from "../../scheduling/eventInstance.js";
import { dayMs, hourMs, minuteMs } from "../../timeConstants.js";
import { TimeOverride } from "../../utils/index.js";
import { isValidNum } from "../../utils/mathUtils.js";
import { RRule, rrulestr } from "../../utils/RRuleLibrary.js";
import { milliseconds, ObjectIdString, RRuleString } from "../../validators/index.js";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { CommandGroup } from "../commandGroup/commandGroup.js";
import type { IrrigationSet } from "../irrigationSet/irrigationSet.js";
import { Organization } from "../organization/organization.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { IrrigationEventFieldsWithDefaults, IrrigationEventSchemaTypes } from "../types/irrigationEvent.d.js";
import { createDelayedRegistrationHook } from "../utils/delayedRegistrationHook.js";
import type { DtoValidatorTypes } from "../validatableClass.js";
import { Zone } from "../zone/zone.js";

const SCHEMA_KEY = "IrrigationEvent" as const;

// v1 capped per-zone start offsets at 30 days to prevent runaway scheduling.
const MAX_ZONE_OFFSET_MS = 30 * dayMs;

/** An irrigation runs for at least a minute, and no longer than 150 hours. */
export const MIN_IRRIGATION_DURATION_MS = minuteMs;
export const MAX_IRRIGATION_DURATION_MS = 150 * hourMs;

export interface ZoneSettingsInput {
    zonesActive: string[];
    zoneWateringPercentages?: Record<string, number>;
    zoneIrrigationOffsets?: Record<string, number>;
}

export interface NormalizedZoneSettings {
    zonesActive: string[];
    zoneWateringPercentages: Record<string, number>;
    zoneIrrigationOffsets: Record<string, number>;
}

/**
 * Search key for tracking whether zone settings normalization is still mutating request DTOs.
 * Keep this easy to grep in logs so we can revisit and potentially remove this normalization later.
 */
export const IRRIGATION_ZONE_SETTINGS_NORMALIZED_LOG_KEY = "IRRIGATION_ZONE_SETTINGS_NORMALIZED" as const;

/**
 * Brings per-zone irrigation settings to a complete, consistent shape: every zone named by any of the
 * three fields ends up in `zonesActive` with a watering percentage and a capped offset.
 *
 * Lives beside the schema rather than in a controller so a request DTO can normalize a body before a
 * handler ever sees it.
 */
export function normalizeZoneSettings(input: ZoneSettingsInput): NormalizedZoneSettings {
    const zoneWateringPercentages = { ...(input.zoneWateringPercentages ?? {}) };
    const zoneIrrigationOffsets = { ...(input.zoneIrrigationOffsets ?? {}) };
    const normalizationReasons = new Set<string>();

    // A zone may be introduced via either list; unify so every zone is fully specified
    const zonesActive = [
        ...new Set([
            ...input.zonesActive,
            ...Object.keys(zoneWateringPercentages),
            ...Object.keys(zoneIrrigationOffsets),
        ]),
    ];
    const zonesActiveChanged =
        zonesActive.length !== input.zonesActive.length ||
        zonesActive.some((zoneId, index) => zoneId !== input.zonesActive[index]);
    if (zonesActiveChanged) {
        normalizationReasons.add("zonesActiveExpandedOrDeduped");
    }

    zonesActive.forEach((zoneId) => {
        if (!Number.isFinite(zoneWateringPercentages[zoneId])) {
            zoneWateringPercentages[zoneId] = 1;
            normalizationReasons.add("zoneWateringPercentageDefaulted");
        }
        if (zoneWateringPercentages[zoneId] <= 0) {
            delete zoneWateringPercentages[zoneId];
            delete zoneIrrigationOffsets[zoneId];
            normalizationReasons.add("zoneWateringPercentageNonPositiveDropped");
            return;
        }
        if (!Number.isFinite(zoneIrrigationOffsets[zoneId])) {
            zoneIrrigationOffsets[zoneId] = 0;
            normalizationReasons.add("zoneOffsetDefaulted");
        } else if (zoneIrrigationOffsets[zoneId] > MAX_ZONE_OFFSET_MS) {
            zoneIrrigationOffsets[zoneId] = MAX_ZONE_OFFSET_MS;
            normalizationReasons.add("zoneOffsetCapped");
        }
    });

    const normalizedZonesActive = zonesActive.filter((zoneId) => zoneWateringPercentages[zoneId] > 0);

    if (normalizationReasons.size > 0) {
        console.info(`${IRRIGATION_ZONE_SETTINGS_NORMALIZED_LOG_KEY}: normalized irrigation zone settings`, {
            key: IRRIGATION_ZONE_SETTINGS_NORMALIZED_LOG_KEY,
            reasons: [...normalizationReasons],
            zonesActiveCount: normalizedZonesActive.length,
        });
    }

    return { zonesActive: normalizedZonesActive, zoneWateringPercentages, zoneIrrigationOffsets };
}

/** Rejects inputs that normalize to no active zones (including empty arrays/objects). */
export function getZonelessSettingsError(input: ZoneSettingsInput): string | null {
    if (normalizeZoneSettings(input).zonesActive.length === 0) {
        return "Neither active zones nor zone watering percentages were provided.";
    }
    return null;
}

// Populates the active-dates cache (the `datesActive` document field and the supplemental
// IrrigationEventUpcomingActiveDates collection) after an IrrigationEvent is saved. The DB-writing
// implementation lives in the backend and is registered in registerHooks.ts once the DB is connected.
export const irrigationActiveDatesPostSave = createDelayedRegistrationHook<
    (event: DocumentType<IrrigationEvent>) => Promise<void>
>("irrigationActiveDatesPostSave");

/**
 * An irrigation's timing, however its ids happen to be typed. Derived from the entity so a rename or
 * retype there is caught here, with only the two reference fields widened: the stored document holds
 * `Ref`s where the response DTO holds strings, and the rules below only ever stringify them.
 */
export type IrrigationTiming = Pick<IrrigationEvent, "rrule" | "defaultDuration"> &
    // Optional here where the entity requires them: the response DTO omits an empty map, and the
    // rules below already read a missing zone as percentage 1, offset 0.
    Partial<Pick<IrrigationEvent, "zoneWateringPercentages" | "zoneIrrigationOffsets">> & {
        irrigationSet?: unknown;
        zonesActive?: readonly { toString(): string }[];
    };

/**
 * Why an irrigation event cannot be stopped mid-run. Shared by the stop endpoint, which sends it as
 * the `code` on its rejection, and by any caller that wants to know before asking.
 */
export enum StopIrrigationBlockedReason {
    /** `defaultDuration` is shared by every occurrence, so shortening one would shorten the rest. */
    REPEATING_EVENT = "REPEATING_EVENT",
    /** A set's events are scheduled together and are not individually stoppable. */
    SET_EVENT = "SET_EVENT",
    /** Each zone runs on its own clock, so one duration cannot stop several of them at one instant. */
    MULTIPLE_ACTIVE_ZONES = "MULTIPLE_ACTIVE_ZONES",
    /** Nothing left to stop: the zone's scaled, offset run has already finished. */
    ALREADY_ENDED = "ALREADY_ENDED",
}

// Stamps SCD Type 2 bookkeeping on every write where it is missing. recordId != _id.
// The inherited fields are required with no schema default, so this has to run on validate: a
// pre("save") hook runs after validation and would be too late.
@pre<IrrigationEvent>("validate", function stampScdBookkeeping(this: DocumentType<IrrigationEvent>) {
    const now = TimeOverride.now();
    if (!this.recordId) this.recordId = new mongoose.Types.ObjectId();
    if (this.versionId == null) this.versionId = 1;
    if (!this.recordCreatedAt) this.recordCreatedAt = now;
    if (!this.versionValidFrom) this.versionValidFrom = now;
    if (!this.versionCreatedAt) this.versionCreatedAt = now;
    if (this.deleted === undefined) this.deleted = false;
    if (this.versionIsCurrent === undefined) this.versionIsCurrent = true;
})
@pre<IrrigationEvent>("save", function saveIrrigation(this: DocumentType<IrrigationEvent>) {
    if (!this.zonesActive) {
        this.zonesActive = [];
    }
    if (
        this.isNew ||
        this.isModified("defaultDuration") ||
        this.isModified("zoneWateringPercentages") ||
        this.isModified("zonesActive") ||
        this.isModified("rrule")
    ) {
        const allKeys = new Set([
            ...this.zonesActive.map((z) => z.toString()),
            ...Object.keys(this.zoneWateringPercentages || {}),
        ]);
        allKeys.forEach((zoneId) => {
            if (!this.zoneWateringPercentages) {
                this.zoneWateringPercentages = {};
            }
            if (!isValidNum(this.zoneWateringPercentages[zoneId])) {
                this.zoneWateringPercentages[zoneId] = 1;
            }
            if (this.zoneWateringPercentages[zoneId] <= 0) {
                // A non-positive percentage means "do not water this zone", but the zero-duration
                // instances it produces read as degenerate data downstream (2026-08-29 production
                // OOM incident). Dropping the zone expresses the same intent without them.
                console.warn("Dropping zone with non-positive watering percentage from irrigation event on save", {
                    irrigationEventId: this._id?.toString(),
                    zoneId,
                    percentage: this.zoneWateringPercentages[zoneId],
                });
                delete this.zoneWateringPercentages[zoneId];
                if (this.zoneIrrigationOffsets) {
                    delete this.zoneIrrigationOffsets[zoneId];
                }
                this.zonesActive = this.zonesActive.filter((z) => z.toString() !== zoneId);
                this.markModified("zoneWateringPercentages");
                this.markModified("zoneIrrigationOffsets");
                return;
            }
            if (this.zoneWateringPercentages[zoneId] > 3) {
                this.zoneWateringPercentages[zoneId] = 3;
            }
            // Note: Removed toMongoId() call as it's a backend-specific extension method
            if (!this.zonesActive.map((z) => z.toString()).includes(zoneId)) {
                this.zonesActive.push(zoneId as any);
            }
        });
    }
})
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "irrigations",
        _id: true,
    },
})
@post<SchemaBase<IrrigationEvent>>(...addSchemaKeyHook(SCHEMA_KEY))
@post<IrrigationEvent>("save", async function refreshActiveDatesCache(this: DocumentType<IrrigationEvent>) {
    await irrigationActiveDatesPostSave.function(this);
})
export class IrrigationEvent extends ScdDocument<IrrigationEvent, IrrigationEventFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public _lastRefreshed?: Date;

    @validate({ zodValidator: z.array(z.string()).optional(), responseOnly: true })
    @prop({ type: () => String, required: true, default: [] }, PropType.ARRAY)
    public datesActive: string[] = [];

    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ ref: () => Zone, required: true, default: [] }, PropType.ARRAY)
    public zonesActive: Ref<Zone>[] = [];

    @validate({ zodValidator: z.record(z.string(), z.number()).optional() })
    @prop({ type: () => Object, allowMixed: Severity.ALLOW, required: true, default: {} })
    public zoneWateringPercentages: Record<string, number> = {};

    @validate({ zodValidator: z.record(z.string(), z.number()).optional() })
    @prop({ type: () => Object, allowMixed: Severity.ALLOW, required: false })
    public zoneIrrigationOffsets?: Record<string, number>;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ ref: () => Organization, required: true })
    public owner!: Ref<Organization>;

    @validate({ zodValidator: ObjectIdString.optional() })
    @prop({ ref: () => CommandGroup, required: false })
    public commandGroup?: Ref<CommandGroup>;

    @validate({
        zodValidator: milliseconds
            .min(MIN_IRRIGATION_DURATION_MS, "Irrigation duration must be at least 1 minute.")
            .max(MAX_IRRIGATION_DURATION_MS, "Irrigation duration must be at most 150 hours.")
            .openapi({
                type: "number",
                minimum: MIN_IRRIGATION_DURATION_MS,
                maximum: MAX_IRRIGATION_DURATION_MS,
                description: "Irrigation duration in milliseconds",
                example: 30 * minuteMs,
            }),
    })
    @prop({
        type: () => Number,
        required: true,
        min: [MIN_IRRIGATION_DURATION_MS, "Irrigation duration must be at least 1 minute."],
        max: [MAX_IRRIGATION_DURATION_MS, "Irrigation duration must be at most 150 hours."],
    })
    public defaultDuration!: number; // milliseconds

    @validate({ zodValidator: RRuleString })
    @prop({ type: () => String, required: true })
    public rrule!: string;

    @validate({ zodValidator: ObjectIdString.optional() })
    @prop({ type: () => mongoose.Types.ObjectId, ref: "IrrigationSet", required: false })
    public irrigationSet?: Ref<IrrigationSet>;

    // Getters are functions that take the schema instance and return a value
    static getters = {
        /**
         * Gets the maximum duration considering all zone watering percentages and offsets
         */
        getMaximumDuration: (self: IrrigationEvent): number => {
            let longestDuration = self.defaultDuration;
            try {
                const zoneKeys = Object.keys(self.zoneWateringPercentages || {});
                if (zoneKeys.length > 0) {
                    longestDuration = 0;
                    zoneKeys.forEach((zone) => {
                        const percentage = self.zoneWateringPercentages[zone];
                        const endOfInstance =
                            (isValidNum(percentage) ? percentage : 1) * self.defaultDuration +
                            (self.zoneIrrigationOffsets?.[zone] || 0);
                        if (endOfInstance > longestDuration) {
                            longestDuration = endOfInstance;
                        }
                    });
                }
            } catch (e) {
                console.error("Error calculating longest duration for irrigation event", e);
                longestDuration = dayMs;
            }
            return longestDuration;
        },
    } as const satisfies Record<string, (self: IrrigationEvent) => any>;

    // Utilities are functions that can take additional parameters beyond the schema instance
    static utilities = {
        /**
         * The reason this irrigation cannot be stopped mid-run, or `undefined` when it can.
         *
         * Stopping shortens `defaultDuration` so the run ends at a chosen instant, which only has a
         * single well-defined meaning for a one-off, standalone, one-zone irrigation still in
         * progress. `now` is passed rather than read from the clock so a caller can ask about a
         * moment other than this one, and so the answer is stable across a render.
         */
        getStopBlockedReason: (self: IrrigationTiming, now: number): StopIrrigationBlockedReason | undefined => {
            if (rrulestr(self.rrule).options.count !== 1) {
                return StopIrrigationBlockedReason.REPEATING_EVENT;
            }
            if (self.irrigationSet) {
                return StopIrrigationBlockedReason.SET_EVENT;
            }
            // `!== 1` rather than `> 1`: the save hook drops a zone whose percentage goes
            // non-positive, so an event can reach here with none left.
            if (self.zonesActive?.length !== 1) {
                return StopIrrigationBlockedReason.MULTIPLE_ACTIVE_ZONES;
            }
            if (IrrigationEvent.utilities.getZoneRunEnd(self, self.zonesActive[0].toString()) <= now) {
                return StopIrrigationBlockedReason.ALREADY_ENDED;
            }
            return undefined;
        },

        /**
         * When a zone's run finishes: the occurrence plus that zone's offset, for `defaultDuration`
         * scaled by its watering percentage. Neither bound is the event's own, so a caller reasoning
         * about `defaultDuration` alone is reasoning about no particular zone.
         */
        getZoneRunEnd: (self: IrrigationTiming, zoneId: string): number => {
            const percentage = self.zoneWateringPercentages?.[zoneId];
            const offsetMs = self.zoneIrrigationOffsets?.[zoneId] ?? 0;
            return (
                rrulestr(self.rrule).options.dtstart.valueOf() +
                offsetMs +
                self.defaultDuration * (isValidNum(percentage) ? percentage : 1)
            );
        },

        /**
         * Gets single commands for a specific zone within a time scope
         */
        getSingleCommands: (self: IrrigationEvent, zone: string, startScope: Date, endScope: Date): EventInstance[] => {
            if (!(self.defaultDuration && self.rrule)) {
                console.error(`ERROR: NO DEFAULT DURATION OR RRULE ON EVENT`);
                return [];
            }
            const eventInstances = Array<EventInstance>();
            const rrule = RRule.fromString(self.rrule);
            const percentage = self.zoneWateringPercentages?.[zone];
            const multiplier = isValidNum(percentage) ? percentage : 1;
            const duration = self.defaultDuration * multiplier;
            const maximumDuration = IrrigationEvent.getters.getMaximumDuration(self);
            const startTimes = rrule.between(new Date(startScope.valueOf() - maximumDuration * 2), endScope);

            let startTimeOffset = 0;
            if (self.zoneIrrigationOffsets && self.zoneIrrigationOffsets[zone]) {
                startTimeOffset = self.zoneIrrigationOffsets[zone];
            }

            startTimes.forEach((startTime) => {
                const newEventInstance = new EventInstance();
                newEventInstance.startDate = new Date(startTime.valueOf() + startTimeOffset);
                newEventInstance.duration = duration / 1000;
                newEventInstance.zone = zone;
                newEventInstance.rootIrrigationID = (self as any)._id?.toString();
                eventInstances.push(newEventInstance);
            });
            return eventInstances;
        },

        // NOTE: The following methods require database access and should be implemented
        // in the backend on the IrrigationEventModel after this schema is integrated:
        //
        // - findByRelevancy(zoneID, date, minHoursInFuture, minHoursInPast)
        //   Finds irrigation events by relevancy to a zone and date
        //
        // - getStateChangesForTimePeriodAndZone(zoneID, date, minHoursInFuture, minHoursInPast, marginToCombineEvents)
        //   Gets a list of eventInstances that have no overlaps and are sorted past to future
        //
        // - getActiveIrrigationAtTime(zoneID, date, minHoursInFuture, minHoursInPast)
        //   Gets the active irrigation event at a specific time
        //
        // - getActiveIrrigationAsFunctionOfTime(zoneID, dateRange, minHoursInFuture, minHoursInPast)
        //   Gets active irrigation as a function of time (returns a function that takes a date)
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<IrrigationEventSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<IrrigationEvent, IrrigationEventFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

IrrigationEvent.dtoValidators = getZodSchemaFromClass(
    IrrigationEvent,
) as unknown as DtoValidatorTypes<IrrigationEventSchemaTypes>;
